import { memo, useState, useCallback, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { TemplateFormFields } from './TemplateFormFields';
import { TemplateSharingSection } from './TemplateSharingSection';
import { TemplateDeleteConfirm } from './TemplateDeleteConfirm';
import { TEMPLATE_VALIDATION } from '@/constants/templateConstants';
import {
  type ITemplateEditorDialogProps,
  type ITemplateFormState,
  getInitialFormState,
} from '@/@types/messageTemplates';

function TemplateEditorDialog({
  open,
  onOpenChange,
  template,
  businessId,
  userBusinesses,
  onSave,
  onDelete,
}: ITemplateEditorDialogProps) {
  const [formState, setFormState] = useState<ITemplateFormState>(() =>
    getInitialFormState(template)
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isEditing = !!template;

  const handleVariableClick = useCallback(
    (variable: string) => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentContent = formState.content;
      const newContent = currentContent.slice(0, start) + variable + currentContent.slice(end);
      setFormState(prev => ({ ...prev, content: newContent }));
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = textarea.selectionEnd = start + variable.length;
      }, 0);
    },
    [formState.content]
  );

  const validateForm = (): string[] => {
    const errors: string[] = [];
    if (!formState.name.trim()) errors.push('Template name is required');
    else if (formState.name.length > TEMPLATE_VALIDATION.NAME_MAX_LENGTH)
      errors.push(`Name must be ${TEMPLATE_VALIDATION.NAME_MAX_LENGTH} characters or less`);
    if (!formState.content.trim()) errors.push('Message content is required');
    else if (formState.content.length > TEMPLATE_VALIDATION.CONTENT_MAX_LENGTH)
      errors.push(`Content must be ${TEMPLATE_VALIDATION.CONTENT_MAX_LENGTH} characters or less`);
    if (formState.channels.length < TEMPLATE_VALIDATION.MIN_CHANNELS)
      errors.push('Select at least one channel');
    return errors;
  };

  const handleSave = async (asDraft: boolean) => {
    const errors = validateForm();
    if (errors.length > 0) {
      alert(errors.join('\n'));
      return;
    }
    setIsSaving(true);
    try {
      const status = asDraft ? ('draft' as const) : ('published' as const);
      const shared = formState.isShareable ? formState.sharedWithBusinessIds : [];
      const common = {
        name: formState.name,
        content: formState.content,
        category: formState.category,
        channels: formState.channels,
        status,
        isShareable: formState.isShareable,
        sharedWithBusinessIds: shared,
      };
      await onSave(
        isEditing && template ? { templateId: template._id, ...common } : { businessId, ...common }
      );
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('Failed to save template. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!template || !onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete(template._id);
      setShowDeleteConfirm(false);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert('Failed to delete template. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Template' : 'New Template'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <TemplateFormFields
              formState={formState}
              setFormState={setFormState}
              textareaRef={textareaRef}
              onVariableClick={handleVariableClick}
            />

            <TemplateSharingSection
              formState={formState}
              setFormState={setFormState}
              businessId={businessId}
              userBusinesses={userBusinesses}
            />
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex-1">
              {isEditing && onDelete && (
                <Button
                  variant="destructive"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isSaving || isDeleting}
                >
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => handleSave(true)}
                disabled={isSaving || isDeleting}
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save as Draft
              </Button>
              <Button onClick={() => handleSave(false)} disabled={isSaving || isDeleting}>
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Template
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TemplateDeleteConfirm
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        template={template}
        isDeleting={isDeleting}
        onDelete={handleDelete}
      />
    </>
  );
}

export const TemplateEditorDialogMemo = memo(TemplateEditorDialog);
